/**
 * Petty Cash Float Summary — every float, its balance against its limit, and what is pending.
 *
 * The at-a-glance report: which boxes need replenishing, and which have vouchers sitting in Draft
 * or waiting on approval.
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, AlertTriangle, Wallet } from 'lucide-react';

import { pettyCashVoucherApi } from '@/api/pettyCashVoucher.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions } from '@/constants/permissions';
import { AppRoutes } from '@/constants/routes';
import { Spinner } from '@/components/ui/Spinner';
import { cn } from '@/utils/cn';

import type { PettyCashFloatSummaryRowDto } from '@/types/pettyCashVoucher.types';

const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const BalanceBar = ({ pct, due }: { pct: number | null; due: boolean }) => {
  if (pct == null) return <span className="text-xs text-slate-400">No limit set</span>;
  const clamped = Math.min(100, Math.max(0, pct));
  return (
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
        <div
          className={cn('h-full rounded-full', due ? 'bg-amber-500' : 'bg-emerald-500')}
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className={cn('text-xs font-mono', due ? 'text-amber-600 dark:text-amber-400' : 'text-slate-500 dark:text-gray-400')}>
        {pct.toFixed(0)}%
      </span>
    </div>
  );
};

export const PettyCashSummaryPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canView = hasPermission(Permissions.PTY_FLOAT_VIEW);

  const [rows, setRows] = useState<PettyCashFloatSummaryRowDto[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!canView) return;
    setLoading(true);
    pettyCashVoucherApi.getSummary()
      .then(r => setRows(r.data.data ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [canView]);

  useEffect(() => { load(); }, [load]);

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-full text-slate-400">
        <p className="text-sm">You do not have permission to view the petty cash summary.</p>
      </div>
    );
  }

  const dueCount = rows.filter(r => r.replenishmentDue).length;

  return (
    <div className="flex h-full flex-col">
      <div className="flex-shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-semibold text-slate-900 dark:text-white">Petty Cash Float Summary</h1>
            <p className="text-xs text-slate-400">
              {rows.length} float{rows.length === 1 ? '' : 's'}
              {dueCount > 0 && (
                <span className="ml-2 text-amber-600 dark:text-amber-400">
                  · {dueCount} need{dueCount === 1 ? 's' : ''} replenishment
                </span>
              )}
            </p>
          </div>
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 dark:text-gray-400
                       hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="md" /></div>
        ) : rows.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-slate-400">
            <p className="text-sm">No floats set up yet.</p>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {rows.map(f => (
              <div key={f.floatId}
                onClick={() => navigate(`${AppRoutes.PETTY_CASH_VOUCHERS}?float=${f.floatId}`)}
                className={cn(
                  'cursor-pointer rounded-xl border bg-white dark:bg-slate-800 p-4 transition-colors',
                  f.replenishmentDue
                    ? 'border-amber-300 dark:border-amber-700'
                    : 'border-slate-200 dark:border-slate-700 hover:border-blue-300 dark:hover:border-blue-600',
                )}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet size={16} className="text-slate-400" />
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">{f.floatName}</p>
                      <p className="text-[11px] font-mono text-slate-400">{f.floatCode}</p>
                    </div>
                  </div>
                  {f.replenishmentDue && (
                    <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-amber-600 dark:text-amber-400">
                      <AlertTriangle size={11} /> Low
                    </span>
                  )}
                </div>

                <p className="mt-3 font-mono text-xl font-bold text-slate-900 dark:text-white">
                  {fmt(f.currentBalance)} <span className="text-sm font-normal text-slate-400">{f.currencyCode}</span>
                </p>
                {f.floatLimit > 0 && (
                  <p className="text-[11px] text-slate-400">of {fmt(f.floatLimit)} limit</p>
                )}

                <div className="mt-2"><BalanceBar pct={f.percentOfLimit} due={f.replenishmentDue} /></div>

                <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500 dark:text-gray-400">
                  <span>{f.custodian ?? 'No custodian set'}</span>
                  <span>Last movement {fmtDate(f.lastMovementDate)}</span>
                </div>

                {(f.draftVoucherCount > 0 || f.pendingApprovalCount > 0) && (
                  <div className="mt-2 flex gap-2">
                    {f.draftVoucherCount > 0 && (
                      <span className="rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:text-slate-300">
                        {f.draftVoucherCount} draft{f.draftVoucherCount === 1 ? '' : 's'}
                      </span>
                    )}
                    {f.pendingApprovalCount > 0 && (
                      <span className="rounded-full bg-sky-100 dark:bg-sky-900/30 px-2 py-0.5 text-[10px] font-medium text-sky-700 dark:text-sky-400">
                        {f.pendingApprovalCount} pending approval
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
